import { application } from 'src';
import { Controller } from '@hotwired/stimulus';
import { findAll, find, getParent, addClass } from '@fretadao/f-js-dom';
import { isPresent } from '@fretadao/f-js-utils';

application.register(
  'move-lines',
  class extends Controller {
    static targets = [
      'listedLinesCheckboxes',
      'linesCheckboxes',
      'linesBox',
      'selectedLinesBox',
      'formFilter',
      'btnApplyFilter',
      'btnUnselectAll',
      'selectedCounter'
    ];

    LIMIT_OF_LINES = 250;

    connect() {
      this.#reloadListedLinesListAndUpdateCounter();
      this.#addEventOnLines();

      document.addEventListener('ajax:success', _event => {
        this.#addEventOnListedLines();
        this.#removeBlockDivOverList();
      });
    }

    disconnect() {
      this.#removeEventOnListedLines();
      this.#removeEventOnLines();
    }

    selectAll() {
      const checkboxes = findAll('tbody tr input[type=checkbox]', this.linesBoxTarget);

      if (this.#countSelectedLines() < this.LIMIT_OF_LINES) {
        this.#notify('Linhas adicionadas', null, 'info');
      } else {
        this.#notifyLimitMessage();
      }

      checkboxes.forEach(checkbox => {
        this.#moveLineToSelectedBox(checkbox, true);
      });

      this.#dispatchAddedLineEvent();
      this.#reloadListedLinesListAndUpdateCounter();

      this.linesBoxTarget.scrollIntoView({ block: 'start' });
    }

    unselectAll() {
      const checkboxElements = findAll('tbody tr input[type=checkbox]', this.selectedLinesBoxTarget);

      checkboxElements.forEach(checkboxEl => {
        this.#removeLineFromSelectedBox(checkboxEl);
      });

      this.#notify('Linhas removidas', null, 'warn');

      this.#reloadListedLinesListAndUpdateCounter();
    }

    replaceDirection(event) {
      this.#defineDirectionFilter({ direction: event.target.value });
      this.unselectAll();
    }

    #dispatchAddedLineEvent() {
      this.selectedLinesBoxTarget.dispatchEvent(new Event('lines-added'));
    }

    #dispatchRemovedLineEvent(lineIds) {
      this.selectedLinesBoxTarget.dispatchEvent(new CustomEvent('lines-removed', { detail: [lineIds].flat() }));
    }

    #defineDirectionFilter({ direction }) {
      const directionFilterField = find('#direction_filter');
      directionFilterField.value = direction;
    }

    #moveLineToSelectedBoxAndReload(event) {
      const lineMoved = this.#moveLineToSelectedBox(event.target);
      if (lineMoved) {
        this.#notifySelectedLine(event);
      } else {
        this.#notifyLimitMessage();
      }
      this.#reloadListedLinesListAndUpdateCounter();
    }

    #moveLineToSelectedBox(field, batch=false) {
      // the max number of selected lines, to not break the limit of query string params
      if (this.#countSelectedLines() >= this.LIMIT_OF_LINES) return;

      const tbodyDesktop = find('#desktop-table-lines-filter tbody', this.selectedLinesBoxTarget);
      const tbodyMobile = find('#mobile-table-lines-filter', this.selectedLinesBoxTarget);
      let desktopField, mobileField;

      if (getParent(field, 4).id === 'desktop-table-lines-filter') {
        desktopField = field;
        mobileField = find(`#mobile-table-lines-filter #${field.id}`, this.linesBoxTarget);
      } else {
        desktopField = find(`#desktop-table-lines-filter #${field.id}`, this.linesBoxTarget);
        mobileField = field;
      }

      const desktopLine = getParent(desktopField, 2);
      const mobileLine = getParent(mobileField, 3);

      tbodyDesktop.append(desktopLine);
      tbodyMobile.append(mobileLine);

      const lineName = this.#lineNameByLine(desktopLine);

      findAll(`#${field.id}`, this.selectedLinesBoxTarget).forEach(field => {
        field.replaceWith(this.#newCheckbox(field, lineName));
      });

      const hiddenField = this.#hiddenFieldOfLine(field.dataset.id, field.id);
      this.formFilterTarget.append(hiddenField);

      if(!batch) this.#dispatchAddedLineEvent();

      return true;
    }

    #lineNameByLine(line) {
      return find('td:nth-child(2)', line).textContent.trim();
    }

    #notifySelectedLine(event) {
      const lineName = this.#lineNameByLine(getParent(find(`#${event.target.id}`), 2));
      this.#notify(lineName, 'Linha adicionada', 'info');
    }

    #notifyUnselectedLine(event) {
      const lineName = this.#lineNameByLine(getParent(find(`#${event.target.id}`), 2));
      this.#notify(lineName, 'Linha removida', 'warn');
    }

    #notifyLimitMessage() {
      this.#notify("Número máximo de linhass selecionadas.\nPara adicionar mais, inicie uma nova solicitação.", null, 'warn');
    }

    #notify(message, title, kind = 'info') {
      const notify = this.application.getControllerForElementAndIdentifier(
        find('#line-schedule-change-request-form'),
        'notifications'
      );
      notify.titleValue = title || '';
      notify.messageValue = message;
      if (kind == 'info') {
        notify.info();
      } else if (kind == 'warn') {
        notify.warn();
      }
    }

    #countSelectedLines() {
      return findAll('tbody tr', this.selectedLinesBoxTarget).length;
    }

    #updateSelectedCounter() {
      this.selectedCounterTarget.innerText = this.#countSelectedLines();
    }

    #newCheckbox(oldField) {
      const newField = document.createElement('input');
      newField.type = 'checkbox';
      newField.value = oldField.value;
      newField.id = oldField.id;
      newField.name = 'line_schedule_change_request[items][]';
      newField.checked = true;
      newField.addEventListener('change', this.#removeLineFromSelectedBoxAndReload.bind(this));
      return newField;
    }

    #hiddenFieldOfLine(lineId, fieldId) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'excluded_lines[]';
      input.value = lineId;
      input.id = fieldId;
      return input;
    }

    #removeLineFromSelectedBoxAndReload(event) {
      this.#notifyUnselectedLine(event);
      this.#removeLineFromSelectedBox(event.target);
      this.#reloadListedLinesListAndUpdateCounter();
    }

    #removeLineFromSelectedBox(field) {
      const lineData = JSON.parse(field.value);
      const checkboxes = findAll(`#${field.id}`, this.selectedLinesBoxTarget);

      checkboxes.forEach(checkbox => {
        if (getParent(checkbox, 2).tagName === 'TR') {
          getParent(checkbox, 2).remove();
        } else {
          getParent(checkbox, 3).remove();
        }
      });

      this.#dispatchRemovedLineEvent(lineData.line_id);

      find(`#hidden_line_${lineData.line_id}`, this.formFilterTarget).remove();
    }

    #reloadListedLinesListAndUpdateCounter() {
      if (!this.hasBtnApplyFilterTarget) return;
      this.#addBlockDivOverList();
      this.btnApplyFilterTarget.click();
      this.#updateSelectedCounter();
    }

    #addBlockDivOverList() {
      const blockDiv = document.createElement('div');
      addClass(
        blockDiv,
        ['absolute', 'blockDiv', 'top-0', 'left-0', 'w-full', 'h-full', 'bg-white', 'bg-opacity-50', 'z-30']
      );
      getParent(this.selectedLinesBoxTarget).prepend(blockDiv);
      getParent(this.linesBoxTarget).prepend(blockDiv.cloneNode());
    }

    #removeBlockDivOverList() {
      const blockDivOverLines = find('.blockDiv', getParent(this.selectedLinesBoxTarget));
      if (blockDivOverLines) blockDivOverLines.remove();
    }

    #addEventOnListedLines() {
      this.listedLinesCheckboxesTargets.forEach(checkbox => {
        checkbox.addEventListener('change', this.#moveLineToSelectedBoxAndReload.bind(this));
      });
    }

    #removeEventOnListedLines() {
      this.listedLinesCheckboxesTargets.forEach(checkbox => {
        checkbox.removeEventListener('change', this.#moveLineToSelectedBoxAndReload.bind(this));
      });
    }

    #addEventOnLines() {
      this.linesCheckboxesTargets.forEach(checkbox => {
        checkbox.addEventListener('change', this.#removeLineFromSelectedBoxAndReload.bind(this));
      });
    }

    #removeEventOnLines() {
      this.linesCheckboxesTargets.forEach(checkbox => {
        checkbox.removeEventListener('change', this.#removeLineFromSelectedBoxAndReload.bind(this));
      });
    }
  }
);
